import { Fragment, useState, type ReactNode } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { BotonDetalle, FILA_ABIERTA, PanelDeDetalle, Senal } from '../../../components/DetalleExpandible';
import { etiquetaBandera } from '../../../domain/pg/catalogo';
import { BANDERAS_YI } from '../../../domain/yichang/catalogo';
import { presenciaModerno, queSeMidioModerno } from '../../../domain/yichang/moderno';
import { etiquetaOpcion } from '../../../domain/yichang/respuestas';
import {
  presenciaBodega, presenciaMercado, queSeMidioBodega, queSeMidioMercado,
} from '../../../domain/yichang/tradicional';
import {
  NOMBRE_MARCA,
  type CierreYiMercado, type FichaYiBodega, type FichaYiMercado, type FichaYiModerno, type InstrumentoYi,
  type MarcaCliente, type MarcaYi, type Presencia,
} from '../../../domain/yichang/tipos';
import DetalleYiModerno from './DetalleYiModerno';
import { DetalleYiBodega, DetalleYiMercado } from './DetalleYiTradicional';
import { PastillaPresencia } from './Respuesta';

const TZ = 'America/Lima';
const hora = (iso: string | null): string => (iso === null ? '' : formatInTimeZone(new Date(iso), TZ, 'HH:mm'));
const SIN = <span className="text-gray-400">—</span>;

export const marcasDe = (m: MarcaYi): MarcaCliente[] => (m === 'las_dos' ? ['pringles', 'kelloggs'] : [m]);

function QueSeMidio({ marca, filas }: {
  marca: MarcaYi;
  filas: (m: MarcaCliente) => { p: Presencia; texto: string };
}) {
  return (
    <div className="space-y-1 text-xs">
      {marcasDe(marca).map((m) => {
        const x = filas(m);
        return (
          <div key={m}>
            <b className="text-gray-800">{NOMBRE_MARCA[m]}</b> <PastillaPresencia p={x.p} />
            <span className="block text-gray-700">{x.texto}</span>
          </div>
        );
      })}
    </div>
  );
}

function Senales({ flags }: { flags: readonly string[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((x) => { const e = etiquetaBandera(x, BANDERAS_YI); return <Senal key={x} tono={e.tono}>{e.label}</Senal>; })}
    </div>
  );
}

/** Una fila plegada y, debajo, su detalle si está abierta. */
function Fila({ id, abierta, setAbierta, columnas, celdas, titulo, sujeto, detalle }: {
  id: string;
  abierta: string | null;
  setAbierta: (v: string | null) => void;
  columnas: number;
  celdas: ReactNode[];
  titulo: string;
  sujeto: string;
  detalle: () => ReactNode;
}) {
  const abierto = abierta === id;
  return (
    <Fragment>
      <tr className={`align-top ${abierto ? FILA_ABIERTA : ''}`}>
        {celdas.map((c, i) => <td key={i} className="px-3 py-2">{c}</td>)}
        <td className="px-3 py-2">
          <BotonDetalle abierto={abierto} onClick={() => setAbierta(abierto ? null : id)} />
        </td>
      </tr>
      {abierto && (
        <tr>
          <td colSpan={columnas} className="p-0">
            <PanelDeDetalle titulo={titulo} sujeto={sujeto} onCerrar={() => setAbierta(null)}>
              {detalle()}
            </PanelDeDetalle>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Cabeza({ titulos }: { titulos: string[] }) {
  return (
    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
      <tr>
        {titulos.map((t) => <th key={t} className="px-3 py-2">{t}</th>)}
        <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
      </tr>
    </thead>
  );
}

const fecha = (f: string, iso: string | null) => (
  <span className="whitespace-nowrap">
    {/* Como cadena: `new Date('2026-09-30')` es medianoche UTC, o sea el día anterior en Lima. */}
    {f}<span className="block text-xs text-gray-500">{hora(iso)}</span>
  </span>
);

/**
 * La lista de fichas de Yichang, una por fila, la más reciente primero.
 *
 * La columna «Qué se midió» sigue al filtro de marca: con las dos marcas trae una línea por marca, y
 * con una sola, solo la suya. Cada línea lleva si la marca se encontró, no se encontró o no se puede
 * saber, con palabras: el color no va solo.
 */
export default function RegistrosYichang({
  instrumento, moderno, bodegas, mercados, cierres, marca, cargando, hayFiltro, onLimpiar, estudioVacio,
}: {
  instrumento: InstrumentoYi;
  moderno: readonly FichaYiModerno[];
  bodegas: readonly FichaYiBodega[];
  mercados: readonly FichaYiMercado[];
  cierres: readonly CierreYiMercado[];
  marca: MarcaYi;
  cargando: boolean;
  hayFiltro: boolean;
  onLimpiar: () => void;
  estudioVacio: boolean;
}) {
  const [abierta, setAbierta] = useState<string | null>(null);
  const total = instrumento === 'moderno' ? moderno.length : instrumento === 'bodegas' ? bodegas.length : mercados.length;

  // Sin filas no se dibuja la tabla nunca: ni vacía mientras carga ni vacía después.
  if (total === 0 && cargando) return <p className="mt-3 text-sm text-gray-600">Cargando las fichas…</p>;
  if (total === 0) {
    return (
      <p className="mt-3 text-sm text-gray-600">
        {hayFiltro ? (
          <>
            El filtro dejó todas las fichas afuera.{' '}
            <button type="button" className="text-blue-700 underline" onClick={onLimpiar}>Limpiar el filtro</button>
          </>
        ) : estudioVacio
          ? 'Todavía no llegó ninguna ficha de Yichang de este instrumento. Cuando el campo envíe la primera, aparece acá.'
          : 'No hay fichas de este instrumento entre esas fechas.'}
      </p>
    );
  }

  const comun = { abierta, setAbierta };

  return (
    // `relative` para que el `sr-only` de la cabecera (posición absoluta) quede dentro del recorte:
    // sin él, en un celular la PÁGINA entera se desplazaba de costado aunque la tabla tuviera su scroll.
    <div className="relative mt-3 overflow-x-auto rounded border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        {instrumento === 'moderno' && (
          <>
            <Cabeza titulos={['Fecha', 'Tienda', 'Cadena', 'Supervisor', 'Qué se midió', 'Señales']} />
            <tbody className="divide-y">
              {moderno.map((f) => (
                <Fila key={f.id} id={f.id} {...comun} columnas={7}
                  titulo="Ficha de la tienda" sujeto={`${f.tiendaTexto ?? 'sin tienda'} · ${f.fecha}`}
                  detalle={() => <DetalleYiModerno ficha={f} marca={marca} />}
                  celdas={[
                    fecha(f.fecha, f.ocurridoEn),
                    <>{f.tiendaTexto ?? SIN}{f.flags.includes('tienda_fuera_de_maestra') && <div className="mt-0.5"><Senal>escrita a mano</Senal></div>}</>,
                    <>{f.cadena ?? SIN}{f.formato !== null && <span className="block text-xs text-gray-500">{f.formato}</span>}</>,
                    f.supervisor ?? SIN,
                    <QueSeMidio marca={marca} filas={(m) => ({ p: presenciaModerno(f, m), texto: queSeMidioModerno(f, m) })} />,
                    <Senales flags={f.flags} />,
                  ]}
                />
              ))}
            </tbody>
          </>
        )}
        {instrumento === 'bodegas' && (
          <>
            <Cabeza titulos={['Fecha', 'Bodega', 'Zona', 'Tipo', 'Supervisor', 'Qué se midió', 'Señales']} />
            <tbody className="divide-y">
              {bodegas.map((f) => (
                <Fila key={f.id} id={f.id} {...comun} columnas={8}
                  titulo="Ficha de la bodega" sujeto={`${f.tiendaNombre ?? 'sin nombre'} · ${f.fecha}`}
                  detalle={() => <DetalleYiBodega ficha={f} marca={marca} />}
                  celdas={[
                    fecha(f.fecha, f.ocurridoEn),
                    <>{f.tiendaNombre ?? SIN}{f.tiendaDireccion !== null && <span className="block text-xs text-gray-500">{f.tiendaDireccion}</span>}</>,
                    <>{f.zona ?? SIN}{f.distrito !== null && <span className="block text-xs text-gray-500">{f.distrito}</span>}</>,
                    <>{f.tipoTienda === null ? SIN : etiquetaOpcion(f.tipoTienda)}{f.modoVisita !== null && <span className="block text-xs text-gray-500">{etiquetaOpcion(f.modoVisita)}</span>}</>,
                    f.supervisor ?? SIN,
                    <QueSeMidio marca={marca} filas={(m) => ({ p: presenciaBodega(f, m), texto: queSeMidioBodega(f, m) })} />,
                    <Senales flags={f.flags} />,
                  ]}
                />
              ))}
            </tbody>
          </>
        )}
        {instrumento === 'mercados' && (
          <>
            <Cabeza titulos={['Fecha', 'Mercado', 'Puesto', 'Supervisor', 'Estado', 'Qué se midió', 'Señales']} />
            <tbody className="divide-y">
              {mercados.map((f) => (
                <Fila key={f.id} id={f.id} {...comun} columnas={8}
                  titulo="Ficha del puesto"
                  sujeto={`${f.mercadoNombre ?? 'sin mercado'} · puesto ${f.puestoNumero ?? '—'} · ${f.fecha}`}
                  detalle={() => <DetalleYiMercado ficha={f} marca={marca} cierres={cierres} />}
                  celdas={[
                    fecha(f.fecha, f.ocurridoEn),
                    <>{f.mercadoNombre ?? SIN}{f.distrito !== null && <span className="block text-xs text-gray-500">{f.distrito}</span>}</>,
                    <>{f.puestoNumero === null ? SIN : <b>#{f.puestoNumero}</b>}{f.puestoNombre !== null && <span className="block text-xs text-gray-500">{f.puestoNombre}</span>}</>,
                    f.supervisor ?? SIN,
                    f.levantado ? 'Levantado' : <Senal>No se pudo · {f.motivoNo ?? 'sin motivo'}</Senal>,
                    <QueSeMidio marca={marca} filas={(m) => ({ p: presenciaMercado(f, m), texto: queSeMidioMercado(f, m) })} />,
                    <Senales flags={f.flags} />,
                  ]}
                />
              ))}
            </tbody>
          </>
        )}
      </table>
    </div>
  );
}
